/**
 * ConversationItem - Single WhatsApp conversation in the list
 */

import { cn } from '../../lib/utils';
import type { WhatsAppConversation } from '@leal-crm/shared';
import { MessageSquare, Image, Mic, Video, FileText, Sticker } from 'lucide-react';

interface ConversationItemProps {
  conversation: WhatsAppConversation;
  isSelected: boolean;
  onClick: () => void;
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Ayer';
  }

  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
}

function MessageTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'image':
      return <Image className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'voice':
      return <Mic className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'video':
      return <Video className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'document':
      return <FileText className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'sticker':
      return <Sticker className="h-3.5 w-3.5 text-muted-foreground" />;
    default:
      return null;
  }
}

function getMessagePreview(conversation: WhatsAppConversation): string {
  const { lastMessage } = conversation;
  switch (lastMessage.type) {
    case 'image':
      return '📷 Foto';
    case 'voice':
      return '🎵 Audio';
    case 'video':
      return '🎥 Vídeo';
    case 'document':
      return '📄 Documento';
    case 'sticker':
      return '🏷️ Sticker';
    default:
      return lastMessage.body || '';
  }
}

export function ConversationItem({
  conversation,
  isSelected,
  onClick,
}: ConversationItemProps) {
  const preview = getMessagePreview(conversation);

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-accent/50',
        isSelected && 'bg-accent'
      )}
    >
      {/* Avatar placeholder */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
        <MessageSquare className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium text-sm">
            {conversation.contactName}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatTimestamp(conversation.lastMessage.timestamp)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <MessageTypeIcon type={conversation.lastMessage.type} />
          <span className="truncate text-xs text-muted-foreground">
            {conversation.lastMessage.fromMe && (
              <span className="text-blue-500">Tú: </span>
            )}
            {preview}
          </span>
          {conversation.unreadCount > 0 && (
            <span className="ml-auto shrink-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-white">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
